/**
 * Per-UDA visual identity, theme data AND canonical registry.
 *
 * This is the SINGLE SOURCE OF TRUTH for the 20 real Urban Development
 * Authorities (source: "ALL UDAs Contacts.xlsx" / ds_dtcp.ds_uda_lst_t).
 * Everything keyed off a UDA — the home map (ModulesSection), the subdomain
 * router (PortalContext), the per-UDA landing theme and the photo gallery —
 * derives from this map.
 *
 * Keyed by the official UDA CODE in lowercase (e.g. `eluda`, `ruda`, `vmrda`).
 * That same key is the subdomain slug: clicking a UDA opens
 * `https://<slug>.<base-domain>/` (e.g. eluda.dreamstep.in) and the portal
 * content site is resolved by `id` (prvdr_ste_id).
 *
 * NOTE: `heroImage` uses a deterministic placeholder (seeded by slug) and the
 * figures in `stats` are indicative placeholders — replace both with official
 * UDA landmark photos and verified figures when available. The page layout,
 * theming and responsiveness do not depend on these exact values.
 */

export interface UdaStat {
  label: string;
  value: string;
  icon: string;
}

export interface UdaTheme {
  slug: string;        // official UDA code, lowercase — also the subdomain slug
  code: string;        // official UDA code, uppercase (e.g. "ELUDA")
  id: number;          // portal content site id (prvdr_ste_id)
  name: string;        // e.g. "Eluru UDA"
  shortName: string;   // e.g. "Eluru"
  fullName: string;    // official authority name
  district: string;
  region: 'Coastal Andhra' | 'Rayalaseema';
  accent: string;      // brand hex
  tagline: string;
  established: string;  // year the UDA / town was constituted
  lat: number;
  lng: number;
  heroImage: string;
  stats: UdaStat[];
}

const seededHero = (slug: string) =>
  `https://picsum.photos/seed/uda-${slug}-hero/1600/720`;

/*
 * Planning Area and Mandals Covered are measured, not indicative: area is the
 * jurisdiction boundary in ds_dtcp.ds_uda_lst_t.bndry_plgn_jn (each UDA's
 * mandals dissolved — see server/migrations/019_uda_boundary_polygon_build.js)
 * and the mandal count is DISTINCT mndl_nm from ds_dtcp.ds_mandal_lst_t. The
 * counts previously here were wrong for every UDA (amuda's '5' was its
 * constituency count). Wards and Population remain unverified placeholders.
 * vk-pcpir and apcrda keep published areas — neither has a derivable boundary.
 */
const makeStats = (area: string, wards: string, population: string, mandals: string): UdaStat[] => [
  { label: 'Planning Area', value: area, icon: 'ri-ruler-2-line' },
  { label: 'Wards / Divisions', value: wards, icon: 'ri-layout-grid-line' },
  { label: 'Population', value: population, icon: 'ri-group-line' },
  { label: 'Mandals Covered', value: mandals, icon: 'ri-map-2-line' },
];

/* The 20 real UDAs. Accent colours are intentionally distinct. */
export const UDA_THEMES: Record<string, UdaTheme> = {
  suda: {
    slug: 'suda', code: 'SUDA', id: 22, name: 'Srikakulam UDA', shortName: 'Srikakulam',
    fullName: 'Srikakulam Urban Development Authority', district: 'Srikakulam',
    region: 'Coastal Andhra', accent: '#0369A1',
    tagline: "Andhra's northern gateway with coastal charm.",
    established: '1983', lat: 18.2949, lng: 83.8938, heroImage: seededHero('suda'),
    stats: makeStats('5,507 km²', '40', '1.6 Lakh', '37'),
  },
  onuda: {
    slug: 'onuda', code: 'ONUDA', id: 35, name: 'Ongole UDA', shortName: 'Ongole',
    fullName: 'Ongole Urban Development Authority', district: 'Prakasam',
    region: 'Coastal Andhra', accent: '#28a103',
    tagline: "Andhra's northern gateway with coastal charm.",
        established: '1983', lat: 15.5057, lng: 80.0499, heroImage: seededHero('onuda'),

        stats: makeStats('16,481 km²', '40', '1.6 Lakh', '51'),

  },
  buda: {
    slug: 'buda', code: 'BUDA', id: 23, name: 'Bobbili UDA', shortName: 'Bobbili',
    fullName: 'Bobbili Urban Development Authority', district: 'Vizianagaram',
    region: 'Coastal Andhra', accent: '#15803D',
    tagline: 'A city of forts, music and gentle growth.',
    established: '1983', lat: 18.5667, lng: 83.4167, heroImage: seededHero('buda'),
    stats: makeStats('2,762 km²', '40', '2.4 Lakh', '14'),
  },
  vmrda: {
    slug: 'vmrda', code: 'VMRDA', id: 10, name: 'Visakhapatnam UDA', shortName: 'Visakhapatnam',
    fullName: 'Visakhapatnam Metropolitan Region Development Authority', district: 'Visakhapatnam',
    region: 'Coastal Andhra', accent: '#0E7490',
    tagline: 'The City of Destiny — planned growth on the eastern coast.',
    established: '1962', lat: 17.6868, lng: 83.2185, heroImage: seededHero('vmrda'),
    stats: makeStats('7,445 km²', '98', '20.3 Lakh', '49'),
  },
  'vk-pcpir': {
    slug: 'vk-pcpir', code: 'VK-PCPIR', id: 36, name: 'VK-PCPIR', shortName: 'VK-PCPIR',
    fullName: 'Visakhapatnam-Kakinada Petroleum, Chemical & Petrochemicals Investment Region',
    district: 'Anakapalli', region: 'Coastal Andhra', accent: '#0D9488',
    tagline: 'A petroleum, chemical and petrochemicals investment corridor.',
    established: '2009', lat: 17.45, lng: 82.85, heroImage: seededHero('vk-pcpir'),
    stats: makeStats('640 km²', '—', '—', '19'),
  },
  kauda: {
    slug: 'kauda', code: 'KAUDA', id: 15, name: 'Kakinada UDA', shortName: 'Kakinada',
    fullName: 'Kakinada Urban Development Authority', district: 'Kakinada',
    region: 'Coastal Andhra', accent: '#047857',
    tagline: 'The Fertilizer City and a thriving deep-water port.',
    established: '1981', lat: 16.9891, lng: 82.2475, heroImage: seededHero('kauda'),
    stats: makeStats('2,911 km²', '50', '4.4 Lakh', '21'),
  },
  ruda: {
    slug: 'ruda', code: 'RUDA', id: 14, name: 'Rajahmundry UDA', shortName: 'Rajahmundry',
    fullName: 'Rajahmundry Urban Development Authority', district: 'East Godavari',
    region: 'Coastal Andhra', accent: '#1D4ED8',
    tagline: 'The Cultural Capital on the mighty Godavari.',
    established: '1980', lat: 17.0005, lng: 81.8040, heroImage: seededHero('ruda'),
    stats: makeStats('3,540 km²', '50', '5.2 Lakh', '26'),
  },
  amuda: {
    slug: 'amuda', code: 'AMUDA', id: 34, name: 'Amalapuram UDA', shortName: 'Amalapuram',
    fullName: 'Amalapuram Urban Development Authority', district: 'Konaseema',
    region: 'Coastal Andhra', accent: '#C2410C',
    tagline: 'The green delta heart of Konaseema.',
    established: '1985', lat: 16.5783, lng: 82.0060, heroImage: seededHero('amuda'),
    stats: makeStats('1,537 km²', '36', '1.4 Lakh', '14'),
  },
  eluda: {
    slug: 'eluda', code: 'ELUDA', id: 19, name: 'Eluru UDA', shortName: 'Eluru',
    fullName: 'Eluru Urban Development Authority', district: 'Eluru',
    region: 'Coastal Andhra', accent: '#0F766E',
    tagline: 'Lakes, wool craft and a calm urban heart.',
    established: '1981', lat: 16.7107, lng: 81.0952, heroImage: seededHero('eluda'),
    stats: makeStats('6,504 km²', '50', '3.0 Lakh', '40'),
  },
  muda: {
    slug: 'muda', code: 'MUDA', id: 30, name: 'Machilipatnam UDA', shortName: 'Machilipatnam',
    fullName: 'Machilipatnam Urban Development Authority', district: 'Krishna',
    region: 'Coastal Andhra', accent: '#0891B2',
    tagline: 'A historic port reborn as a coastal growth hub.',
    established: '1983', lat: 16.1875, lng: 81.1389, heroImage: seededHero('muda'),
    stats: makeStats('2,516 km²', '50', '1.7 Lakh', '13'),
  },
  apcrda: {
    slug: 'apcrda', code: 'APCRDA', id: 11, name: 'AP Capital Region (Amaravati)', shortName: 'Amaravati',
    fullName: 'Andhra Pradesh Capital Region Development Authority', district: 'Guntur',
    region: 'Coastal Andhra', accent: '#B91C1C',
    tagline: 'The capital region on the banks of the Krishna.',
    established: '2014', lat: 16.5062, lng: 80.6480, heroImage: seededHero('apcrda'),
    // APCRDA has no rows in ds_dtcp.ds_mandal_lst_t, so its mandal count and
    // boundary cannot be derived like the others' — shown as unknown rather
    // than as a literal zero. Area here is the published figure, not measured.
    stats: makeStats('8,352 km²', '—', '58 Lakh', '—'),
  },
  pauda: {
    slug: 'pauda', code: 'PAUDA', id: 25, name: 'Palnadu UDA', shortName: 'Palnadu',
    fullName: 'Palnadu Urban Development Authority', district: 'Palnadu',
    region: 'Coastal Andhra', accent: '#CA8A04',
    tagline: 'The heart of the Palnadu, rising fast.',
    established: '1985', lat: 16.2346, lng: 80.0487, heroImage: seededHero('pauda'),
    stats: makeStats('5,945 km²', '36', '1.5 Lakh', '23'),
  },
  bauda: {
    slug: 'bauda', code: 'BAUDA', id: 37, name: 'Bapatla UDA', shortName: 'Bapatla',
    fullName: 'Bapatla Urban Development Authority', district: 'Bapatla',
    region: 'Coastal Andhra', accent: '#65A30D',
    tagline: 'A serene coastal town with planned growth.',
    established: '1986', lat: 15.9044, lng: 80.4675, heroImage: seededHero('bauda'),
    stats: makeStats('1,468 km²', '34', '1.2 Lakh', '9'),
  },
  nuda: {
    slug: 'nuda', code: 'NUDA', id: 16, name: 'Nellore UDA', shortName: 'Nellore',
    fullName: 'Nellore Urban Development Authority', district: 'Nellore',
    region: 'Coastal Andhra', accent: '#7C3AED',
    tagline: 'Aquaculture, agriculture and planned expansion.',
    established: '1980', lat: 14.4426, lng: 79.9865, heroImage: seededHero('nuda'),
    stats: makeStats('10,700 km²', '54', '6.0 Lakh', '37'),
  },
  kdpuda: {
    slug: 'kdpuda', code: 'KDPUDA', id: 21, name: 'Kadapa UDA', shortName: 'Kadapa',
    fullName: 'Kadapa Urban Development Authority', district: 'YSR Kadapa',
    region: 'Rayalaseema', accent: '#9333EA',
    tagline: 'Steel, stone and a steadily modernising city.',
    established: '1981', lat: 14.4674, lng: 78.8241, heroImage: seededHero('kdpuda'),
    stats: makeStats('15,139 km²', '50', '3.4 Lakh', '51'),
  },
  kuda: {
    slug: 'kuda', code: 'KUDA', id: 17, name: 'Kurnool UDA', shortName: 'Kurnool',
    fullName: 'Kurnool Urban Development Authority', district: 'Kurnool',
    region: 'Rayalaseema', accent: '#BE123C',
    tagline: 'The Gateway to Rayalaseema, the erstwhile state capital.',
    established: '1980', lat: 15.8281, lng: 78.0373, heroImage: seededHero('kuda'),
    stats: makeStats('17,358 km²', '52', '5.3 Lakh', '54'),
  },
  tuda: {
    slug: 'tuda', code: 'TUDA', id: 13, name: 'Tirupati UDA', shortName: 'Tirupati',
    fullName: 'Tirupati Urban Development Authority', district: 'Tirupati',
    region: 'Rayalaseema', accent: '#B45309',
    tagline: 'The Temple City — sacred heritage, planned future.',
    established: '1981', lat: 13.6288, lng: 79.4192, heroImage: seededHero('tuda'),
    stats: makeStats('9,255 km²', '50', '5.9 Lakh', '38'),
  },
  chuda: {
    slug: 'chuda', code: 'CHUDA', id: 33, name: 'Chittoor UDA', shortName: 'Chittoor',
    fullName: 'Chittoor Urban Development Authority', district: 'Chittoor',
    region: 'Rayalaseema', accent: '#DC2626',
    tagline: 'Mango orchards and a fast-growing border city.',
    established: '1984', lat: 13.2172, lng: 79.1003, heroImage: seededHero('chuda'),
    stats: makeStats('4,358 km²', '50', '1.9 Lakh', '23'),
  },
  pkmuda: {
    slug: 'pkmuda', code: 'PKMUDA', id: 38, name: 'Palamaner-Kuppam-Madanapalle UDA', shortName: 'Madanapalle',
    fullName: 'Palamaner - Kuppam - Madanapalle Urban Development Authority', district: 'Annamayya',
    region: 'Rayalaseema', accent: '#DB2777',
    tagline: 'The cool highland belt of southern Rayalaseema.',
    established: '1986', lat: 13.5503, lng: 78.5029, heroImage: seededHero('pkmuda'),
    stats: makeStats('4,252 km²', '—', '2.0 Lakh', '18'),
  },
  puda: {
    slug: 'puda', code: 'PUDA', id: 39, name: 'Puttaparthi UDA', shortName: 'Puttaparthi',
    fullName: 'Puttaparthi Urban Development Authority', district: 'Sri Sathya Sai',
    region: 'Rayalaseema', accent: '#2563EB',
    tagline: 'A spiritual town of global pilgrimage.',
    established: '1985', lat: 14.1652, lng: 77.8120, heroImage: seededHero('puda'),
    stats: makeStats('1,615 km²', '—', '1.0 Lakh', '6'),
  },
  ahuda: {
    slug: 'ahuda', code: 'AHUDA', id: 18, name: 'Ananthapur-Hindupur UDA', shortName: 'Anantapur',
    fullName: 'Ananthapur Hindupur Urban Development Authority', district: 'Anantapur',
    region: 'Rayalaseema', accent: '#A16207',
    tagline: 'Solar power and resilient growth in the dry belt.',
    established: '1982', lat: 14.6819, lng: 77.6006, heroImage: seededHero('ahuda'),
    stats: makeStats('17,156 km²', '50', '4.1 Lakh', '57'),
  },
};

/**
 * The names in the map above are only stand-ins. What a UDA is CALLED comes from
 * the one UDA master, ds_uda_lst_t.uda_nm (the same name every admin screen
 * shows); main.tsx reads it before the first render and calls this. `name` and
 * `fullName` become that name; `shortName` is its place part, for sentences
 * that name the town ("…areas of Nellore").
 */
export function applyUdaMasterNames(rows: Array<{ uda_cd?: string; uda_nm?: string }>): void {
  for (const r of rows || []) {
    const t = r?.uda_cd ? UDA_THEMES[r.uda_cd.toLowerCase()] : undefined;
    const nm = (r?.uda_nm || '').trim();
    if (!t || !nm) continue;
    t.name = nm;
    t.fullName = nm;
    t.shortName = nm.replace(/\s+(UDA|MRDA|DA|PCPIR)$/i, '').trim() || nm;
  }
}

/* Lookup helpers ----------------------------------------------------------- */
export const getUdaThemeBySlug = (slug?: string | null): UdaTheme | null =>
  slug ? UDA_THEMES[slug.toLowerCase()] ?? null : null;

export const getUdaThemeById = (id: number): UdaTheme | null =>
  Object.values(UDA_THEMES).find((t) => t.id === id) ?? null;

/**
 * Resolve the UDA theme for the CURRENT host from the subdomain (first label),
 * e.g. ruda.dreamstep.in / ruda.local → RUDA theme. Returns null for the DTCP
 * main site or any unknown host (→ default orange). Used to apply the UDA
 * accent across the whole portal (every page on the subdomain), not just home.
 */
export const getUdaThemeForHost = (): UdaTheme | null => {
  if (typeof window === 'undefined') return null;
  const first = window.location.hostname.split('.')[0]?.toLowerCase();
  return first ? getUdaThemeBySlug(first) : null;
};

/* Parse a 3- or 6-digit hex into [r,g,b]. */
const toRgb = (hex: string): [number, number, number] => {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  const num = parseInt(full, 16);
  return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
};

const toHex = (r: number, g: number, b: number): string =>
  `#${((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b)).toString(16).slice(1)}`;

/**
 * Darken a hex colour by mixing toward black. Used to derive the gradient/
 * hover shade from a single accent so the data stays minimal.
 */
export const darken = (hex: string, amount = 0.2): string => {
  const [r, g, b] = toRgb(hex);
  return toHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
};

/** Lighten a hex colour by mixing toward white. */
export const lighten = (hex: string, amount = 0.25): string => {
  const [r, g, b] = toRgb(hex);
  return toHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
};

/** Soft tint of an accent (mix mostly white) for backgrounds. p = white share. */
export const tint = (hex: string, p = 0.9): string => lighten(hex, p);

/** Two-letter monogram for the emblem crest (e.g. "Visakhapatnam" → "VI"). */
export const monogram = (name: string): string =>
  name.replace(/UDA/i, '').trim().slice(0, 2).toUpperCase();

/** Emblem-crest code for a UDA — the official UDA code. */
export const udaCode = (slug: string, name: string): string =>
  getUdaThemeBySlug(slug)?.code || monogram(name);

/**
 * Build the full set of accent CSS variables for a given accent colour.
 * Set on an ancestor element so the Header and all shared portal sections
 * (which read var(--portal-accent*)) adopt the UDA's colour. Cast to
 * React.CSSProperties at the call site.
 */
export const accentVars = (accent: string): Record<string, string> => {
  const dark = darken(accent, 0.28);
  return {
    '--portal-accent': accent,
    '--portal-accent-dark': dark,
    '--portal-accent-light': lighten(accent, 0.22),
    '--portal-accent-soft': tint(accent, 0.9),
    '--portal-accent-soft2': tint(accent, 0.8),
    '--uda-accent': accent,
    '--uda-accent-dark': dark,
  };
};
